"use client";

import {
  Navbar as HeroUINavbar,
  NavbarContent,
  NavbarMenu,
  NavbarBrand,
  NavbarItem,
  NavbarMenuItem,
} from "@heroui/navbar";
import { link as linkStyles } from "@heroui/theme";
import NextLink from "next/link";
import Image from "next/image";
import clsx from "clsx";
import { useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { RiLoginCircleLine } from "react-icons/ri";
import { RxHamburgerMenu } from "react-icons/rx";

import { ThemeSwitch } from "@/components/theme-switch";
import LanguageSwitch from "@/components/language-switch";
import { Link as NextIntlLink, usePathname } from "@/i18n/navigation";
import { logEvent } from "@/services/analytics";
import { getKeyString } from "@/utils/common";
import { siteConfig } from "@/config/site";
import { appContainerClass } from "@/utils/layout";
export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations("Navigation");
  // Locale used to construct cross-origin links to app.faxify.com so the
  // user lands on the same language they're browsing — and to avoid the
  // 307-to-/{locale} redirect that Ahrefs flags for SEO.
  const locale = useLocale();
  const appUrl = `${siteConfig.appUrl}/${locale}`;
  // Current path (locale-stripped by next-intl) → drives the active nav pill.
  const pathname = usePathname();
  const isActiveRoute = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  // Navbar ref for styling
  const navbarRef = useRef<HTMLDivElement | null>(null);

  // Get navigation items from site config
  const navRoutes = siteConfig.navItems;

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
  };

  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <HeroUINavbar
      ref={navbarRef as any}
      className="z-50 bg-background"
      // Use the shared app container class so the navbar's logo + menu
      // align with the page's <main> and the footer. maxWidth="full"
      // disables HeroUI's theme-driven max-width so our `wrapper` class
      // is the single source of truth.
      classNames={{
        wrapper: appContainerClass,
      }}
      isMenuOpen={isMenuOpen}
      maxWidth="full"
      position="sticky"
      onMenuOpenChange={handleMenuOpenChange}
    >
      <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
        <NavbarItem>
          <NavbarBrand as="div" className="gap-3 max-w-fit">
            <NextIntlLink
              className="flex justify-start items-center gap-1"
              href="/"
              onClick={() => logEvent(getKeyString("home_logo_click"))}
            >
              <Image
                priority
                alt="Faxify Logo"
                className="rounded-md"
                height={40}
                src="/logo.png"
                width={40}
              />
              <p className="font-bold text-3xl">Faxify</p>
            </NextIntlLink>
          </NavbarBrand>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent className="hidden lg:flex gap-4" justify="center">
        {navRoutes.map(route => (
          <NavbarItem key={route.href}>
            <NextIntlLink
              className={clsx(
                linkStyles({ color: "foreground" }),
                "text-xl font-semibold data-[active=true]:bg-primary data-[active=true]:text-primary-foreground",
                "rounded-full px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-background hover:bg-primary hover:text-primary-foreground active:bg-primary-700"
              )}
              color="foreground"
              data-active={isActiveRoute(route.href)}
              href={route.href}
              onClick={() => logEvent(getKeyString(`nav_${route.label}_click`))}
            >
              {t(route.label as keyof typeof t)}
            </NextIntlLink>
          </NavbarItem>
        ))}
      </NavbarContent>

      <NavbarContent
        className="hidden lg:flex basis-1/5 lg:basis-full"
        justify="end"
      >
        <NavbarItem className="hidden lg:flex">
          <ThemeSwitch />
        </NavbarItem>
        <NavbarItem className="hidden lg:flex">
          <LanguageSwitch />
        </NavbarItem>
        <NavbarItem className="hidden lg:flex">
          <NextLink
            aria-label="Login to Faxify application"
            href={appUrl}
            target="_blank"
            title="Login to Faxify application"
            onClick={() => logEvent(getKeyString("login_button_click"))}
          >
            <RiLoginCircleLine className="dark:text-gray-300" size={24} />
          </NextLink>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent className="lg:hidden basis-1 pl-2 gap-1" justify="end">
        <NavbarItem>
          <ThemeSwitch />
        </NavbarItem>
        <NavbarItem>
          <LanguageSwitch />
        </NavbarItem>
        <NavbarItem>
          <NextLink
            aria-label="Login to Faxify application"
            href={appUrl}
            target="_blank"
            title="Login to Faxify application"
            onClick={() => logEvent(getKeyString("mobile_login_button_click"))}
          >
            <RiLoginCircleLine className="dark:text-gray-300" size={24} />
          </NextLink>
        </NavbarItem>
        <NavbarItem>
          <button
            aria-label="Toggle menu"
            className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center rounded-md hover:bg-default-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <RxHamburgerMenu size={24} />
          </button>
        </NavbarItem>
      </NavbarContent>

      <NavbarMenu
        className={clsx(
          "flex flex-col z-50 border-t border-default-200 bg-background"
        )}
      >
        <div className="mx-4 mt-2 flex flex-col gap-2">
          {navRoutes.map((route, index) => (
            <NavbarMenuItem key={`${route.href}-${index}`}>
              <NextIntlLink
                className={clsx(
                  linkStyles({ color: "foreground" }),
                  "block w-[calc(100%+2rem)] -mx-4 rounded-lg px-4 py-3 text-2xl font-semibold hover:bg-primary hover:text-primary-foreground active:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-background transition-colors data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                )}
                color="foreground"
                data-active={isActiveRoute(route.href)}
                href={route.href}
                onClick={() => {
                  handleMenuItemClick();
                  logEvent(getKeyString(`mobile_nav_${route.label}_click`));
                }}
              >
                {t(route.label as keyof typeof t)}
              </NextIntlLink>
            </NavbarMenuItem>
          ))}
        </div>
      </NavbarMenu>
    </HeroUINavbar>
  );
};
